import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  IconButton,
  Typography
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import { updateReservation, fetchPendingReservations, fetchCustomers } from "../../../../services/pos/Tables";

const ReservationEditDialog = ({
  open,
  onClose,
  reservationId,
  reservations,
  setReservations,
  onEdit,
}) => {
  const [formData, setFormData] = useState({
    id: "",
    customer_name: "",
    customer_phone1: "",
    customer_phone2: "",
    customer_address1: "",
    customer_address2: "",
    email: "",
    table_id: "",
    reservation_date: "",
    reservation_time: "",
    number_of_people: "",
    status: "pending",
  });

  useEffect(() => {
    if (reservationId) {
      fetchPendingReservations()
        .then((data) => {
          const res = Array.isArray(data) ? data.find((r) => r.id === reservationId) : null;
          if (res) {
            setFormData({ ...res });
            if (res.customer_id) {
              fetchCustomers([res.customer_id])
                .then((customerData) => {
                  const customer = customerData.find((c) => c.id === res.customer_id);
                  if (customer) {
                    setFormData((prev) => ({
                      ...prev,
                      customer_name: customer.name || "",
                      customer_phone1: customer.phone1 || "",
                      customer_address1: customer.address1 || "",
                    }));
                  }
                })
                .catch((err) => console.error("❌ خطأ في جلب بيانات العميل:", err.message));
            }
          }
        })
        .catch((err) => {
          console.error("❌ خطأ في جلب بيانات الحجز:", err.message);
        });
    }
  }, [reservationId, open]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "number_of_people" && value < 1) return; // منع القيم السالبة
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleConfirmEdit = () => {
    // تأكد من أن الوقت بالتنسيق الصحيح HH:mm
    let time = formData.reservation_time || "";
    if (time.length === 5 && /^\d{2}:\d{2}$/.test(time)) {
      // صحيح
    } else if (time.length === 8 && /^\d{2}:\d{2}:\d{2}$/.test(time)) {
      // إذا كان الوقت مع ثواني، احذف الثواني
      time = time.slice(0, 5);
    } else {
      // إذا لم يكن بالتنسيق الصحيح، أعد ضبطه أو أظهر رسالة خطأ
      alert("وقت الحجز يجب أن يكون بالتنسيق HH:mm");
      return;
    }

    updateReservation(formData.id, { ...formData, reservation_time: time })
      .then(() => {
        const updatedReservation = { ...formData, reservation_time: time };
        setReservations((prev) =>
          prev.map((res) =>
            res.id === updatedReservation.id ? updatedReservation : res
          )
        );
        if (onEdit) onEdit(updatedReservation); // تمرير البيانات الجديدة إلى ReservationsSection
        onClose();
      })
      .catch((err) => {
        let msg = "❌ فشل في تعديل الحجز: ";
        if (err?.message) msg += err.message;
        if (err?.errors?.reservation_time?.[0]) msg += " - " + err.errors.reservation_time[0];
        alert(msg);
        console.error(msg);
      });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>تعديل بيانات الحجز</DialogTitle>
      <DialogContent>
        <Grid container spacing={2} mt={1}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="اسم العميل"
              name="customer_name"
              value={formData.customer_name || ""}
              onChange={handleChange}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="هاتف 1"
              name="customer_phone1"
              value={formData.customer_phone1 || ""}
              onChange={handleChange}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="هاتف 2"
              name="customer_phone2"
              value={formData.customer_phone2 || ""}
              onChange={handleChange}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="العنوان 1"
              name="customer_address1"
              value={formData.customer_address1 || ""}
              onChange={handleChange}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="العنوان 2"
              name="customer_address2"
              value={formData.customer_address2 || ""}
              onChange={handleChange}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="البريد الإلكتروني"
              name="email"
              value={formData.email || ""}
              onChange={handleChange}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="تاريخ الحجز"
              name="reservation_date"
              type="date"
              value={formData.reservation_date || ""}
              onChange={handleChange}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="وقت الحجز"
              name="reservation_time"
              type="time"
              value={formData.reservation_time || ""}
              onChange={handleChange}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="عدد الضيوف"
              name="number_of_people"
              type="number"
              value={formData.number_of_people || ""}
              onChange={handleChange}
              inputProps={{ min: 0 }} // منع القيم السالبة باستخدام خاصية HTML
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="الحالة"
              name="status"
              value={formData.status || ""}
              onChange={handleChange}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="inherit" variant="outlined">
          إلغاء
        </Button>
        <Button onClick={handleConfirmEdit} color="primary" variant="contained">
          حفظ التعديلات
        </Button>
      </DialogActions>
    </Dialog>
  );
};
export default ReservationEditDialog;